import { useCallback, useRef } from 'react';
import {
  Alert,
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useLocalization } from '@/hooks/useLocalization';
import type { InsertInvitationTemplateVariable } from '../constants/invitationTemplateVariables';
import InvitationMessageRichTextEditor from './InvitationMessageRichTextEditor';
import InvitationTemplateVariableList from './InvitationTemplateVariableList';

type Props = {
  editHtml: string;
  setEditHtml: (v: string) => void;
  useLastName: boolean;
  setUseLastName: (v: boolean) => void;
  customSignature: string;
  setCustomSignature: (v: string) => void;
  useGroupName: boolean;
  setUseGroupName: (v: boolean) => void;
  advisorName?: string;
  advisorGroupName?: string;
};

const InvitationCustomEditor = ({
  editHtml,
  setEditHtml,
  useLastName,
  setUseLastName,
  customSignature,
  setCustomSignature,
  useGroupName,
  setUseGroupName,
  advisorName,
  advisorGroupName,
}: Props) => {
  const { t } = useLocalization();
  const insertVariableRef = useRef<InsertInvitationTemplateVariable>(
    () => undefined,
  );
  const handleEditorReady = useCallback(
    (insertVariable: InsertInvitationTemplateVariable) => {
      insertVariableRef.current = insertVariable;
    },
    [],
  );

  return (
    <Stack gap={2}>
      <Alert severity='info'>
        {t(
          'bulkInvite.defaultNote',
          'Anything marked with (default) can be changed during the actual invitation process.',
        )}
      </Alert>

      <FormControl component='fieldset'>
        <FormLabel
          component='legend'
          sx={{ color: 'primary.main' }}
        >
          {t('bulkInvite.salutation', 'Salutation')}{' '}
          {t('bulkInvite.defaultMarker', '(default)')}
        </FormLabel>
        <RadioGroup
          row
          value={useLastName ? 'last' : 'first'}
          onChange={(e) => {
            setUseLastName(e.target.value === 'last');
          }}
          sx={{ mt: 0.5 }}
        >
          <FormControlLabel
            value='first'
            control={<Radio />}
            label={t('bulkInvite.useFirstName', 'Use First Name')}
          />
          <FormControlLabel
            value='last'
            control={<Radio />}
            label={t(
              'bulkInvite.useFirstAndLastName',
              'Use First and Last Name',
            )}
          />
        </RadioGroup>
      </FormControl>

      <Box>
        <Typography
          variant='subtitle1'
          sx={{ mb: 1 }}
        >
          <b>{t('bulkInvite.message', 'Message')}</b>
        </Typography>
        <Box
          className='wa-invite-vars__container'
          sx={{ mb: 1.5 }}
        >
          <Box
            className='wa-invite-vars'
            sx={{ width: '100%', maxWidth: 720 }}
          >
            <Typography
              variant='body2'
              color='text.secondary'
              className='wa-invite-vars__intro'
            >
              {t(
                'bulkInvite.variablesIntro',
                'Create a template by using these variables. Use the variables where you would want the actual value to appear.',
              )}
            </Typography>
            <InvitationTemplateVariableList
              onInsert={(token) => {
                insertVariableRef.current(token);
              }}
            />
            <Typography
              variant='caption'
              color='text.secondary'
              className='wa-invite-vars__note'
            >
              {t(
                'bulkInvite.variablesNote',
                "Note: the above variables must be included in the message body for Advisor 's to be able to modify them during the invitation phase.",
              )}
            </Typography>
          </Box>
        </Box>
        <Box className='wa-invite-richtext'>
          <InvitationMessageRichTextEditor
            value={editHtml}
            onChange={setEditHtml}
            onReady={handleEditorReady}
          />
        </Box>
      </Box>

      <TextField
        label={`${t('bulkInvite.signature', 'Signature')} ${t('bulkInvite.defaultMarker', '(default)')}`}
        value={customSignature}
        onChange={(e) => {
          setCustomSignature(e.target.value);
        }}
        fullWidth
      />

      <FormControl component='fieldset'>
        <FormLabel
          component='legend'
          sx={{ color: 'primary.main' }}
        >
          {t('bulkInvite.sender', 'Sender')}
        </FormLabel>
        <Typography
          variant='body2'
          color='text.secondary'
          sx={{ mb: 1 }}
        >
          {t(
            'bulkInvite.senderHint',
            'The email body says who sent the invitation twice, choose who you want to be the invitor.',
          )}
        </Typography>
        <RadioGroup
          value={useGroupName ? 'group' : 'advisor'}
          onChange={(e) => {
            setUseGroupName(e.target.value === 'group');
          }}
        >
          <FormControlLabel
            value='group'
            control={<Radio />}
            label={t('bulkInvite.sendFromName', {
              defaultValue: `Send email from ${advisorGroupName ?? ''}`,
              name: advisorGroupName ?? '',
            })}
          />
          <FormControlLabel
            value='advisor'
            control={<Radio />}
            label={t('bulkInvite.sendFromName', {
              defaultValue: `Send email from ${advisorName ?? ''}`,
              name: advisorName ?? '',
            })}
          />
        </RadioGroup>
      </FormControl>
    </Stack>
  );
};

export default InvitationCustomEditor;
